import React, { useCallback, useMemo, useState } from 'react';
import { message, Spin } from 'antd';
import type { TCredentialingContract, TCredentialingContractView, TCredentialingCoveredPanel } from 'types/credentialing';
import type { SettingEnum } from 'types/enumerationData';
import { EnumFields } from 'types/enumerationData';
import { markCredentialingSignatureSigned } from 'api/credentialing';
import useContractBanner from 'hooks/credentialing/useContractBanner';
import useCredentialingContracts from 'hooks/credentialing/useCredentialingContracts';
import useGetSettingData from 'hooks/useGetSettingData';
import { getStateDisplayName } from 'utils/common';
import dayjs from 'utils/dayjs';
import SignContractModal from 'pages/dashboard/CredentialingPage/components/SignContractModal';
import s from './s.module.less';

type Props = {
    isGroup?: boolean;
    onSigned?: () => void;
};

const PILL_CONFIG: Record<string, [string, string, string]> = {
    SIGNATURE_PENDING: ['Needs your signature', '#FEF3C7', '#92400E'],
    SIGNATURE_NOT_RECEIVED: ['Signature not received', '#FEF3C7', '#92400E'],
    SIGNATURE_VERIFYING: ['Verifying signature', '#DBEAFE', '#1D4ED8'],
    SIGNATURE_AWAITING_COUNTERSIGN: ['Awaiting countersign', '#DBEAFE', '#1D4ED8'],
    SIGNATURE_EXECUTED: ['Fully signed', '#DBEAFE', '#1D4ED8'],
    SIGNATURE_WITHDRAWN_BY_PAYER: ['Withdrawn by payer', '#F3F4F6', '#374151'],
    SIGNATURE_LINK_EXPIRED: ['Link expired', '#F3F4F6', '#374151'],
};

const parsePIN = (payerResults?: string): string | undefined => {
    if (!payerResults) return undefined;
    try {
        const parsed = JSON.parse(payerResults);
        return parsed?.pin;
    } catch {
        return undefined;
    }
};

const formatDate = (iso?: string): string => {
    if (!iso) return '';
    return dayjs(iso).format('MMM D, YYYY');
};

const FORCE_DOWNLOAD = false;

const SvgPdf = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
    </svg>
);

const SvgDownload = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
);

const SvgWarning = () => (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="#92400E">
        <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
    </svg>
);

const PanelRow = ({ panel }: { panel: TCredentialingCoveredPanel }) => {
    const pin = parsePIN(panel.payerResults);
    const isApproved = panel.panelStatus === 'APPROVED';
    let detail = '';
    if (isApproved && pin && panel.payerEffectiveAt) {
        detail = `PIN ${pin} · Effective ${formatDate(panel.payerEffectiveAt)}`;
    } else if (isApproved && pin) {
        detail = `PIN ${pin}`;
    } else if (isApproved && panel.payerEffectiveAt) {
        detail = `Effective ${formatDate(panel.payerEffectiveAt)}`;
    }
    return (
        <div className={s.panelRow}>
            <span className={s.panelName}>{panel.providerName || `Provider ${panel.providerId}`}</span>
            {isApproved ? (
                <>
                    <span className={s.panelApproved}>Approved</span>
                    {detail && <span className={s.panelDetail}>{detail}</span>}
                </>
            ) : (
                <span className={s.panelAwaiting}>Awaiting approval</span>
            )}
        </div>
    );
};

const ContractsSection = ({ isGroup, onSigned }: Props) => {
    const view: TCredentialingContractView = isGroup ? 'practice' : 'provider';
    const [contractsList, contractsLoading, refetchContracts] = useCredentialingContracts(view);
    const [bannerList, , refetchBanner] = useContractBanner(view);
    const [stateList] = useGetSettingData(EnumFields.STATE);
    const stateListTyped = stateList as SettingEnum[] | undefined;
    const [activeState, setActiveState] = useState<string | null>(null);
    const [signingTarget, setSigningTarget] = useState<TCredentialingContract | null>(null);
    const [confirmLoading, setConfirmLoading] = useState(false);

    const handleDownloadFile = useCallback(async (url: string, fileName: string) => {
        try {
            const res = await fetch(url);
            if (!res.ok) {
                message.error('File download failed. Please try again.');
                return;
            }
            const blob = await res.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(blobUrl);
        } catch {
            message.error('File download failed. Please try again.');
        }
    }, []);

    const signUrlMap = useMemo(() => {
        const map = new Map<number, string>();
        bannerList
            .filter((b) => b.signUrl)
            .forEach((b) => {
                if (b.contractId) map.set(b.contractId, b.signUrl!);
            });
        return map;
    }, [bannerList]);

    const states = useMemo(() => {
        const seen: string[] = [];
        contractsList.forEach((c) => {
            if (!seen.includes(c.payerState)) seen.push(c.payerState);
        });
        return seen;
    }, [contractsList]);

    const effectiveState = states.includes(activeState || '') ? activeState! : states[0] || '';

    const rows = useMemo(
        () => contractsList.filter((c) => c.payerState === effectiveState),
        [contractsList, effectiveState],
    );

    const handleConfirmSigned = async () => {
        if (!signingTarget) return;
        setConfirmLoading(true);
        try {
            const res = await markCredentialingSignatureSigned(
                signingTarget.contractId
                    ? { contractId: signingTarget.contractId }
                    : { panelId: signingTarget.panelId },
            );
            if (res && !res.error) {
                if (res.data?.data === false) {
                    message.warning('We could not confirm this contract. Please refresh and try again.');
                } else {
                    message.success('Contract signed status updated');
                }
                setSigningTarget(null);
                refetchContracts();
                refetchBanner();
                onSigned?.();
            }
        } finally {
            setConfirmLoading(false);
        }
    };

    if (contractsLoading && contractsList.length === 0) {
        return <Spin style={{ display: 'block', padding: '40px 0' }} />;
    }

    if (contractsList.length === 0) {
        return (
            <div className={s.emptyText}>
                No contracts yet. When a payer approves you they issue a contract to sign; it appears here straight away, and the fully signed copies are saved once the payer countersigns.
            </div>
        );
    }

    const sigLabel = isGroup ? "Needs your practice's signature" : 'Needs your signature';

    return (
        <div className={s.wrap}>
            <div className={s.sectionHeader}>
                <div className={s.sectionTitle}>Contracts</div>
            </div>
            {states.length > 1 && (
                <div className={s.stateChips}>
                    {states.map((st) => {
                        const cnt = contractsList.filter((c) => c.payerState === st).length;
                        return (
                            <span
                                key={st}
                                className={`${s.stateChip}${st === effectiveState ? ` ${s.active}` : ''}`}
                                onClick={() => setActiveState(st)}
                            >
                                {getStateDisplayName(st, stateListTyped)} <span className={s.chipCount}>{cnt}</span>
                            </span>
                        );
                    })}
                </div>
            )}
            <div className={s.rows}>
                {rows.map((c, i) => {
                    const needsSig = c.status === 'SIGNATURE_PENDING' || c.status === 'SIGNATURE_NOT_RECEIVED';
                    const hasMultiplePanels = isGroup && c.coveredPanels && c.coveredPanels.length > 1;
                    const pin = hasMultiplePanels ? undefined : parsePIN(c.payerResults);
                    const isApproved = c.status === 'SIGNATURE_EXECUTED' && !!c.payerEffectiveAt;
                    let pillCfg: [string, string, string] = isApproved
                        ? ['Approved', '#DCFCE7', '#166534']
                        : (PILL_CONFIG[c.status] || ['Approved', '#DCFCE7', '#166534']);
                    if (c.status === 'SIGNATURE_PENDING') pillCfg = [sigLabel, '#FEF3C7', '#92400E'];
                    if (hasMultiplePanels && c.status === 'SIGNATURE_EXECUTED') {
                        const allApproved = c.coveredPanels.every((p) => p.panelStatus === 'APPROVED');
                        if (!allApproved) pillCfg = PILL_CONFIG.SIGNATURE_EXECUTED || ['Fully signed', '#DBEAFE', '#1D4ED8'];
                    }
                    const hasFiles = (c.status === 'SIGNATURE_EXECUTED') && c.files.length > 0;

                    let dateText = '';
                    if (!hasMultiplePanels) {
                        if (isApproved && pin) {
                            dateText = `PIN ${pin} · Effective ${formatDate(c.payerEffectiveAt)}`;
                        } else if (isApproved) {
                            dateText = `Effective ${formatDate(c.payerEffectiveAt)}`;
                        } else if (c.status === 'SIGNATURE_AWAITING_COUNTERSIGN' && c.selfReportedSignedAt) {
                            dateText = `Signed ${formatDate(c.selfReportedSignedAt)}`;
                        } else if (c.status === 'SIGNATURE_EXECUTED' && c.executedAt) {
                            dateText = `Countersigned ${formatDate(c.executedAt)}`;
                        }
                    }

                    let subtitle = '';
                    if (c.status === 'SIGNATURE_PENDING') {
                        subtitle = `Sign it and ${c.payerName} countersigns. A few minutes.${c.signByAt ? ` Sign by ${formatDate(c.signByAt)}.` : ''}`;
                    } else if (c.status === 'SIGNATURE_NOT_RECEIVED') {
                        subtitle = `${c.payerName} has not received the signed copy. Review it and check it was submitted.`;
                    } else if (c.status === 'SIGNATURE_WITHDRAWN_BY_PAYER') {
                        subtitle = `${c.payerName} withdrew this contract and is issuing a new one.`;
                    } else if (c.status === 'SIGNATURE_LINK_EXPIRED') {
                        subtitle = 'The signing link expired after 30 days. We are requesting a new contract.';
                    }

                    const contractSignUrl = signUrlMap.get(c.contractId);
                    const canSignHere = needsSig && c.canSign && !!contractSignUrl;

                    return (
                        <div key={`${c.contractId}-${c.panelId}-${i}`} className={`${s.row}${i > 0 ? ` ${s.withBorder}` : ''}`}>
                            <div className={s.rowMain}>
                                <div className={s.rowHeader}>
                                    <span className={s.payerName}>{c.payerName}</span>
                                    {needsSig ? (
                                        <span className={s.attentionBadge}>
                                            <SvgWarning />
                                            {pillCfg[0]}
                                        </span>
                                    ) : (
                                        <span
                                            className={s.statusPill}
                                            style={{ background: pillCfg[1], color: pillCfg[2] }}
                                        >
                                            {pillCfg[0]}
                                        </span>
                                    )}
                                    {dateText && <span className={s.dateText}>{dateText}</span>}
                                </div>
                                {subtitle && <div className={s.subtitle}>{subtitle}</div>}
                                {hasMultiplePanels && (
                                    <div className={s.panelRows}>
                                        {c.coveredPanels.map((panel) => (
                                            <PanelRow key={panel.panelId} panel={panel} />
                                        ))}
                                    </div>
                                )}
                                {hasFiles && (
                                    <div className={s.fileRow}>
                                        {c.files.map((f) => (
                                            FORCE_DOWNLOAD ? (
                                                <span
                                                    key={f.kind}
                                                    className={s.fileChip}
                                                    title={f.fileName}
                                                    onClick={() => handleDownloadFile(f.downloadUrl, f.fileName)}
                                                >
                                                    <SvgPdf />
                                                    <span className={s.fileChipText}>
                                                        <b>{f.kind === 'AGREEMENT' ? 'Agreement' : 'Fee schedule'}</b>{' '}
                                                        <span className={s.fileChipName}>{f.fileName}</span>
                                                    </span>
                                                    <span className={s.fileChipDl}><SvgDownload /></span>
                                                </span>
                                            ) : (
                                                <a
                                                    key={f.kind}
                                                    className={s.fileChip}
                                                    title={f.fileName}
                                                    href={f.downloadUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <SvgPdf />
                                                    <span className={s.fileChipText}>
                                                        <b>{f.kind === 'AGREEMENT' ? 'Agreement' : 'Fee schedule'}</b>{' '}
                                                        <span className={s.fileChipName}>{f.fileName}</span>
                                                    </span>
                                                    <span className={s.fileChipDl}><SvgDownload /></span>
                                                </a>
                                            )
                                        ))}
                                    </div>
                                )}
                            </div>
                            {canSignHere && (
                                <span className={s.signBtn} onClick={() => setSigningTarget(c)}>
                                    Review and sign
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>

            <SignContractModal
                open={!!signingTarget}
                payerName={signingTarget?.payerName || ''}
                state={signingTarget?.payerState || ''}
                signingUrl={signingTarget ? signUrlMap.get(signingTarget.contractId) : undefined}
                confirmLoading={confirmLoading}
                onClose={() => setSigningTarget(null)}
                onConfirm={handleConfirmSigned}
            />
        </div>
    );
};

export default ContractsSection;
